import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useBlogPosts } from '../hooks/useContent';

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTag = searchParams.get('tag') || '';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(initialTag);

  // Fetch blog posts from Contentful (with static fallback)
  const { posts: blogPosts, isLoading, error } = useBlogPosts();

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set();
    blogPosts.forEach((post) => {
      const postTags = Array.isArray(post.tags) ? post.tags : [];
      postTags.forEach((tag) => tags.add(tag));
    });
    return Array.from(tags);
  }, [blogPosts]);

  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesSearch =
        !searchQuery ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()));
      const postTags = Array.isArray(post.tags) ? post.tags : [];
      const matchesTag = !selectedTag || postTags.includes(selectedTag);
      return matchesSearch && matchesTag;
    });
  }, [searchQuery, selectedTag, blogPosts]);

  const handleTagChange = (tag) => {
    if (tag === selectedTag) {
      setSelectedTag('');
      searchParams.delete('tag');
    } else {
      setSelectedTag(tag);
      searchParams.set('tag', tag);
    }
    setSearchParams(searchParams);
  };

  return (
    <div data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
              Insights
            </p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-brand-primary mb-6">
              Blog
            </h1>
            <p className="text-slate-600 text-lg">
              Thoughts, strategies, and insights on leadership, personal growth, and organizational
              transformation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b border-slate-100 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full border-slate-200"
                data-testid="blog-search-input"
              />
            </div>

            {/* Tags */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar w-full md:w-auto">
              <Filter className="w-5 h-5 text-slate-400 flex-shrink-0" />
              {allTags.map((tag) => (
                <Button
                  key={tag}
                  variant={selectedTag === tag ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleTagChange(tag)}
                  className={`rounded-full flex-shrink-0 ${
                    selectedTag === tag
                      ? 'bg-brand-primary text-white'
                      : 'text-slate-600 hover:text-brand-primary'
                  }`}
                  data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {tag}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
              <span className="ml-2 text-slate-600">Loading articles...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-16">
              <p className="text-red-500 mb-4">Failed to load articles: {error}</p>
              <Button variant="outline" onClick={() => window.location.reload()} className="rounded-full">
                Try Again
              </Button>
            </div>
          )}

          {/* Posts Grid */}
          {!isLoading && !error && filteredPosts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`} className="block">
                    <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {(Array.isArray(post.tags) ? post.tags : []).slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="text-xs font-medium text-brand-secondary bg-brand-secondary/10 px-3 py-1 rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <h2 className="font-heading text-xl font-bold text-brand-primary mb-2 group-hover:text-brand-secondary transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        <p className="text-slate-600 text-sm line-clamp-2">{post.excerpt}</p>
                        <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                          <span>{post.date}</span>
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}

          {/* No Results */}
          {!isLoading && !error && filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg mb-4">No articles found matching your criteria.</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedTag('');
                  searchParams.delete('tag');
                  setSearchParams(searchParams);
                }}
                className="rounded-full"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
